import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class CountPointsInPolygonsDB extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.countpointsinpolygons',
        group: 'Spatial Analysis',
        title: 'ST Count Points in Polygons',
        description: `
      This component counts the number of points that intersect with each polygon on the input table.
      `,
        starred: true
      },
      [
        {
          name: 'points',
          type: WorkflowComponentParamType.InputTable,
          title: 'Points table'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geopoints',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in points table',
          parent: 'points',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'idcol',
          type: WorkflowComponentParamType.Column,
          title: 'Column with identifier in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)

    const leftOutputTableName = connection.quoteName(outputTablename + '_left')
    const rightOutputTableName = connection.quoteName(outputTablename + '_right')

    const select = dedent`WITH points AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopoints}) */
      *, ${connection.processInputGeogColumn(preparedParams.geopoints as string)} AS ${
      preparedParams.geopoints
    }_processed FROM ${leftOutputTableName}
    ),
    polygons AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopolygons}) */
      *, ${connection.processInputGeogColumn(preparedParams.geopolygons as string)} AS ${
      preparedParams.geopolygons
    }_processed FROM ${rightOutputTableName}
    )
    SELECT /*+ REPARTITION(40) */
              polygonsb.*,
              COALESCE(__count.count, 0) AS count
            FROM ${connection.quoteName(params.polygons as string)} polygonsb
            LEFT JOIN
              (
                SELECT /*+ REPARTITION(40) */
                  polygons.${params.idcol} poligonid,
                  count(*) count
                FROM
                  points,
                  polygons
                WHERE
                    (points.__carto_bounds_xmax >= polygons.__carto_bounds_xmin
                    AND points.__carto_bounds_xmin <= polygons.__carto_bounds_xmax
                    AND points.__carto_bounds_ymax >= polygons.__carto_bounds_ymin
                    AND points.__carto_bounds_ymin <= polygons.__carto_bounds_ymax)
                  AND
                    ST_INTERSECTS(
                      polygons.${preparedParams.geopolygons}_processed,
                      points.${preparedParams.geopoints}_processed
                    )
                GROUP BY
                    polygons.${params.idcol}
              ) __count
            ON __count.poligonid = polygonsb.${params.idcol}`

    const createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
      connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    )
    const generateSpatialFilterTablesQuery = (connection as DBricksConnection).generateSpatialFilterTables(
      connection.quoteName(preparedParams.points as string),
      connection.quoteName(preparedParams.polygons as string),
      preparedParams.geopoints as string,
      preparedParams.geopolygons as string,
      leftOutputTableName,
      rightOutputTableName
    )
    const query = `${generateSpatialFilterTablesQuery}\n${createTable}`

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.points, {
      name: 'count',
      type: ColumnDataType.Number
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.DatabricksRest]
  }
}

export { CountPointsInPolygonsDB }
